import React, { useState, useRef, useEffect } from "react";
import { trackSettingsChange } from "../../services/activityTracker";
import {
  Row, Col, Card, CardBody, Container, Input, Label, Button,
} from "reactstrap";

const ORANGE      = "#ff6b35";
const ORANGE_DARK = "#e85a28";
const ARCHIVE_COLOR = "#c97c3a";

// ── Helpers ────────────────────────────────────────────────────────────────
const UNIT_OPTIONS = ["Days", "Months", "Years"];

const toDays = (value, unit) => {
  const v = parseInt(value) || 0;
  if (unit === "Months") return Math.round(v * 30.44);
  if (unit === "Years") return v * 365;
  return v;
};

const pageHeadingStyle = { color: "var(--vz-body-color)" };
const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
const pageIconStyle = { color: ORANGE, fontSize: 16 };
const chipStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `1px solid ${toRgba(ORANGE, 0.55)}`,
  background: `${ORANGE}10`,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};


const formatLabel = (value, unit) => {
  const v = parseInt(value) || 0;
  return v === 0 ? "0" : `${v} ${unit}`;
};

const retentionLabel = (hv, hu, wv, wu) => {
  const h = parseInt(hv) || 0;
  const w = parseInt(wv) || 0;

  const first = h > 0 ? `${h} ${hu}` : "";
  const second = w > 0 ? `${w} ${wu}` : "";

  return `${first} ${second}`.trim();
};

// const totalDaysLabel = (hv, hu, wv, wu) => {
//   const d = toDays(hv, hu) + toDays(wv, wu);
//   if (d === 0) return "0 Days";
//   if (d >= 365 && d % 365 === 0) return `${d / 365} Year${d / 365 > 1 ? "s" : ""}`;
//   const months = Math.floor(d / 30);
//   const days = d % 30;
//   if (months > 0 && days > 0) return `${months} Month${months > 1 ? "s" : ""} ${days} Day${days > 1 ? "s" : ""}`;
//   if (months > 0) return `${months} Month${months > 1 ? "s" : ""}`;
//   return `${d} Day${d > 1 ? "s" : ""}`;
// };

// ── Custom dropdown ────────────────────────────────────────────────────────
const UnitDropdown = ({ value, onChange, accentColor }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 110 });
  const [bgColor, setBgColor] = useState("#1e1e2e");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Read the real computed background once on mount
  useEffect(() => {
    if (triggerRef.current) {
      const computed = getComputedStyle(document.documentElement)
        .getPropertyValue("--vz-card-bg").trim();
      if (computed) setBgColor(computed);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpen = () => {
    // Always recalculate position fresh on every open
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
      // Also re-read bg in case theme changed
      const computed = getComputedStyle(document.documentElement)
        .getPropertyValue("--vz-card-bg").trim();
      if (computed) setBgColor(computed);
    }
    setOpen(o => !o);
  };

  return (
    <div style={{ width: 110, userSelect: "none" }}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "7px 10px",
          borderRadius: 6,
          cursor: "pointer",
          border: `1px solid ${open ? accentColor : "var(--vz-border-color)"}`,
          backgroundColor: "var(--vz-card-bg)",
          color: "var(--vz-body-color)",
          fontSize: 13,
          fontWeight: 600,
          transition: "border-color 0.15s",
          boxShadow: open ? `0 0 0 2px ${accentColor}22` : "none",
          gap: 4,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </span>
        <i
          className="ri-arrow-down-s-line"
          style={{
            fontSize: 16, color: accentColor, flexShrink: 0, lineHeight: 1,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </div>

      {/* Options — position:fixed so it escapes every stacking context and never clips behind other rows */}
      {open && (
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            width: coords.width,
            backgroundColor: bgColor || "#1e1e2e",
            border: `1px solid ${accentColor}`,
            borderRadius: 6,
            overflow: "hidden",
            zIndex: 99999,
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          }}
        >
          {UNIT_OPTIONS.map(u => {
            const isSelected = u === value;
            return (
              <div
                key={u}
                onClick={() => { onChange(u); setOpen(false); }}
                style={{
                  padding: "9px 14px",
                  fontSize: 13,
                  cursor: "pointer",
                  color: isSelected ? accentColor : "var(--vz-body-color)",
                  backgroundColor: isSelected ? `${accentColor}20` : bgColor || "#1e1e2e",
                  fontWeight: isSelected ? 700 : 400,
                  borderLeft: isSelected ? `3px solid ${accentColor}` : "3px solid transparent",
                  transition: "background 0.1s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = `${accentColor}12`; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = bgColor || "#1e1e2e"; }}
              >
                {u}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ── Generic reusable dropdown (same style as UnitDropdown) ─────────────────
const SelectDropdown = ({ value, onChange, options, width = 130 }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width });
  const [bgColor, setBgColor] = useState("#1e1e2e");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const computed = getComputedStyle(document.documentElement)
      .getPropertyValue("--vz-card-bg").trim();
    if (computed) setBgColor(computed);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpen = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
      const computed = getComputedStyle(document.documentElement)
        .getPropertyValue("--vz-card-bg").trim();
      if (computed) setBgColor(computed);
    }
    setOpen(o => !o);
  };

  return (
    <div style={{ width, userSelect: "none" }}>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 10px", borderRadius: 6, cursor: "pointer",
          border: `1px solid ${open ? ORANGE : "var(--vz-border-color)"}`,
          backgroundColor: "var(--vz-card-bg)", color: "var(--vz-body-color)",
          fontSize: 13, fontWeight: 600, transition: "border-color 0.15s",
          boxShadow: open ? `0 0 0 2px ${ORANGE}22` : "none", gap: 4,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </span>
        <i
          className="ri-arrow-down-s-line"
          style={{
            fontSize: 16, color: ORANGE, flexShrink: 0, lineHeight: 1,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </div>

      {open && (
        <div
          ref={menuRef}
          style={{
            position: "fixed", top: coords.top, left: coords.left, width: coords.width,
            backgroundColor: bgColor || "#1e1e2e",
            border: `1px solid ${ORANGE}`, borderRadius: 6,
            overflow: "hidden", zIndex: 99999,
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          }}
        >
          {options.map(opt => {
            const isSelected = opt === value;
            return (
              <div
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                style={{
                  padding: "9px 14px", fontSize: 13, cursor: "pointer",
                  color: isSelected ? ORANGE : "var(--vz-body-color)",
                  backgroundColor: isSelected ? `${ORANGE}20` : bgColor || "#1e1e2e",
                  fontWeight: isSelected ? 700 : 400,
                  borderLeft: isSelected ? `3px solid ${ORANGE}` : "3px solid transparent",
                  transition: "background 0.1s", whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = `${ORANGE}12`; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = bgColor || "#1e1e2e"; }}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Left panel setting items ───────────────────────────────────────────────
const SETTINGS_ITEMS = [
  {

    key:         "retention",
    title:       "Retention Policy",
    description: "How long your log data should be retained. Changes to this retention period will affect all records.",
    badge:       null,
    icon:        "ri-time-line",
  },
  {
    key:         "archiving",
    title:       "Data Archiving",
    description: "Automatically archive older records to cold storage before deletion to preserve audit history.",
    badge:       "Disabled",
    icon:        "ri-archive-line",
  },
  {
    key:         "purge_schedule",
    title:       "Purge Schedule",
    description: "Configure when the automatic purge job runs to remove records beyond the retention window.",
    badge:       "Daily · 02:00 AM",
    icon:        "ri-calendar-check-line",
  },
  {
    key:         "export_before_purge",
    title:       "Export Before Purge",
    description: "Automatically export records to CSV or JSON before they are permanently deleted.",
    badge:       "Disabled",
    icon:        "ri-download-cloud-line",
  }
];

// ── Left setting card ──────────────────────────────────────────────────────
const SettingCard = ({ item, isSelected, dynamicBadge, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const badge = dynamicBadge ?? item.badge;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--vz-secondary-bg, #fff)",
        border: `1px solid ${isSelected ? ORANGE : hovered ? "rgba(255,107,53,0.35)" : "var(--vz-border-color)"}`,
        borderRadius: 10, padding: "16px 18px", marginBottom: 10,
        cursor: "pointer", transition: "all 0.2s ease",
        boxShadow: isSelected ? `0 2px 12px rgba(255,107,53,0.12)` : "0 1px 4px rgba(0,0,0,0.04)",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-3">
        <div className="d-flex gap-3 align-items-start">
           <span style={chipStyle}>
          <i className={item.icon} style={{
            fontSize: 18, marginTop: 2, flexShrink: 0,
            color: isSelected ? ORANGE : "var(--vz-secondary-color)",
          }} />
          </span>
          <div>
            <div className="fw-semibold mb-1" style={{ fontSize: 14 }}>{item.title}</div>
            <p className="text-muted mb-0" style={{ fontSize: 12, lineHeight: 1.55 }}>{item.description}</p>
          </div>
        </div>
        {badge && (
          <span style={{
            fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
            color: item.key === "retention" ? ORANGE : "var(--vz-secondary-color)",
          }}>
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Retention right panel ──────────────────────────────────────────────────
const RetentionPanel = ({
  activeRetention,
  setActiveRetention,
  archiveRetention,
  setArchiveRetention,

  // REQUIRED props (fix for undefined variables)
  archiveEnabled,
  purgeFrequency,
  purgeTime,
  exportEnabled,
  exportFormat,
}) => {

  const [active, setActive] = useState(activeRetention || { value: 14, unit: "Days" });
  const [archive, setArchive] = useState(archiveRetention || { value: 3, unit: "Months" });
  const [showConfirm, setShowConfirm] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync state if parent changes
  useEffect(() => {
    if (activeRetention) setActive(activeRetention);
  }, [activeRetention]);

  useEffect(() => {
    if (archiveRetention) setArchive(archiveRetention);
  }, [archiveRetention]);

  const activeDays = toDays(active.value, active.unit);
  const archiveDays = toDays(archive.value, archive.unit);
  const totalD = activeDays + archiveDays;

  const activePct =
    totalD > 0 ? Math.max(Math.round((activeDays / totalD) * 100), 5) : 15;
  const archivePct = 100 - activePct;

  // Save retention policy
  const handleSave = async () => {
    try {
      const authUser = JSON.parse(sessionStorage.getItem("authUser"));
      const token = authUser?.token;

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/retention`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            active_value: active.value,
            active_unit: active.unit,
            archive_value: archive.value,
            archive_unit: archive.unit,

            archive_enabled: archiveEnabled,
            purge_frequency: purgeFrequency,
            purge_time: purgeTime,
            export_enabled: exportEnabled,
            export_format: exportFormat,
          }),
        }
      );

      const data = await res.json();

      if (data.success) {
        setActiveRetention(active);
        setArchiveRetention(archive);

        setSaved(true);
        setShowConfirm(false);
        setTimeout(() => setSaved(false), 3000);

        trackSettingsChange("Log Retention", {
          active_retention: `${active.value} ${active.unit}`,
          archive_retention: `${archive.value} ${archive.unit}`,
          archive_enabled: archiveEnabled,
          purge_frequency: purgeFrequency,
          export_enabled: exportEnabled,
          export_format: exportFormat,
        });
      }
    } catch (err) {
      console.error("Error saving retention:", err);
    }
  };


  return (
    <>
      <h6 className="fw-bold mb-1" style={{ fontSize: 15 }}>Retention Policy</h6>
      <p className="text-muted mb-4" style={{ fontSize: 12, lineHeight: 1.6 }}>
        How long your log data should be retained. Changes to this retention period will affect all records.
      </p>

      {/* Total */}
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fw-semibold" style={{ fontSize: 13 }}>Total retention</span>
        <span className="fw-bold" style={{ fontSize: 14, color: ORANGE }}>
          {retentionLabel(active.value, active.unit, archive.value, archive.unit)}
          <i className="ri-information-line ms-2 text-muted" style={{ fontSize: 12, fontWeight: 400 }} />
        </span>
      </div>
      <hr className="my-3" />

      {/* Searchable */}
      <p className="text-muted mb-3" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 700 }}>
        Searchable
      </p>

      {/* Active row */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: ORANGE, flexShrink: 0 }} />
          <Label className="mb-0 fw-medium" style={{ fontSize: 13 }}>
            Active <i className="ri-information-line ms-1 text-muted" style={{ fontSize: 11 }} />
          </Label>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Input
            type="number" min="0" value={active.value}
            onChange={e => setActive(p => ({ ...p, value: e.target.value }))}
            style={{ width: 70, textAlign: "center", fontWeight: 700, fontSize: 14, padding: "6px 8px" }}
          />
          <UnitDropdown value={active.unit} onChange={v => setActive(p => ({ ...p, unit: v }))} accentColor={ORANGE} />
        </div>
      </div>

      {/* Archive row */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: ARCHIVE_COLOR, flexShrink: 0 }} />
          <Label className="mb-0 fw-medium" style={{ fontSize: 13 }}>
            Archive <i className="ri-information-line ms-1 text-muted" style={{ fontSize: 11 }} />
          </Label>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Input
            type="number" min="0" value={archive.value}
            onChange={e => setArchive(p => ({ ...p, value: e.target.value }))}
            style={{ width: 70, textAlign: "center", fontWeight: 700, fontSize: 14, padding: "6px 8px" }}
          />
          <UnitDropdown value={archive.unit} onChange={v => setArchive(p => ({ ...p, unit: v }))} accentColor={ARCHIVE_COLOR} />
        </div>
      </div>

      {/* Non-Searchable */}
      <hr className="my-3" />
      <p className="text-muted mb-3" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 700 }}>
        Non-Searchable
      </p>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: "#6c757d", flexShrink: 0 }} />
          <Label className="mb-0 fw-medium" style={{ fontSize: 13 }}>
            Purge <i className="ri-information-line ms-1 text-muted" style={{ fontSize: 11 }} />
          </Label>
        </div>
        <span className="text-muted" style={{ fontSize: 13 }}>After total retention period</span>
      </div>

      {/* Snapsactive */}
      <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>Your Retention Policy Snapsactive</p>

      <div>
        <div style={{
          textAlign: "center", fontSize: 10,
          color: "var(--vz-secondary-color)",
          borderBottom: "1px solid var(--vz-border-color)",
          paddingBottom: 4, marginBottom: 8,
        }}>
          Searchable
        </div>

        {/* ── Bar ── */}
        <div style={{
          display: "flex", height: 52, borderRadius: 6,
          overflow: "hidden", border: "1px solid var(--vz-border-color)",
        }}>
          {/* Active — orange gradient with white text */}
          <div style={{
            width: `${activePct}%`,
            background: "linear-gradient(135deg, #ff6b35 0%, #e85a28 100%)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700, color: "#ffffff",
            padding: "0 8px", transition: "width 0.35s ease", flexShrink: 0,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
          }}>
            <span>Active</span>
            <span style={{ fontWeight: 400, fontSize: 10, opacity: 0.85 }}>
              {formatLabel(active.value, active.unit)}
            </span>
          </div>

          {/* Archive — archive amber, distinct from orange */}
          <div style={{
            width: `${archivePct}%`,
            background: "#ffffff",
            borderLeft: "2px solid rgba(0,0,0,0.08)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700, color: "#1a1a2e",
            padding: "0 8px", transition: "width 0.35s ease",
          }}>
            <span>Archive</span>
            <span style={{ fontWeight: 400, fontSize: 10, opacity: 0.85 }}>
              {formatLabel(archive.value, archive.unit)}
            </span>
          </div>
        </div>

        {/* Start / End */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="text-muted" style={{ fontSize: 10 }}>Start</span>
          <div style={{
            flex: 1, height: 1, margin: "0 8px",
            borderTop: "1px dashed var(--vz-border-color)", position: "relative",
          }}>
            <i className="ri-arrow-right-line" style={{
              position: "absolute", right: -6, top: -9,
              fontSize: 12, color: "var(--vz-secondary-color)",
            }} />
          </div>
          <span className="text-muted" style={{ fontSize: 10 }}>
            End: {retentionLabel(active.value, active.unit, archive.value, archive.unit)}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="mt-3 mb-4 p-3" style={{
        backgroundColor: "rgba(255,107,53,0.07)",
        border: "1px solid rgba(255,107,53,0.2)",
        borderRadius: 8, fontSize: 11, lineHeight: 1.7,
      }}>
        <i className="ri-information-line me-2" style={{ color: ORANGE }} />
        <span className="text-muted">
          The <strong style={{ color: ORANGE }}>Active</strong> tier is optimised for fast search.
          The <strong style={{ color: ARCHIVE_COLOR }}>Archive</strong> tier retains data at lower cost before final deletion.
          Once data passes the total retention period it will be scheduled for permanent deletion and cannot be recovered.
        </span>
      </div>

      {/* Actions */}
      {!showConfirm ? (
        <div className="d-flex justify-content-end gap-2">
          <Button color="light" style={{ fontSize: 13 }}
            onClick={() => { setActive({ value: 14, unit: "Days" }); setArchive({ value: 3, unit: "Months" }); }}>
            Cancel
          </Button>
          <Button
            onClick={() => setShowConfirm(true)}
            style={{ backgroundColor: ORANGE, borderColor: ORANGE, fontWeight: 600, fontSize: 13, minWidth: 80 }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = ORANGE_DARK; e.currentTarget.style.borderColor = ORANGE_DARK; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = ORANGE; e.currentTarget.style.borderColor = ORANGE; }}
          >
            Save
          </Button>
        </div>
      ) : (
        <div className="alert alert-danger p-3 mb-0" style={{ borderRadius: 8 }}>
          <p className="fw-semibold mb-1" style={{ fontSize: 13 }}>
            <i className="ri-alert-line me-2" />Confirm change
          </p>
          <p className="mb-3" style={{ fontSize: 12, lineHeight: 1.55 }}>
            Records beyond <strong>{retentionLabel(active.value, active.unit, archive.value, archive.unit)}</strong> will be{" "}
            <strong>permanently deleted</strong> on the next purge cycle. This cannot be undone.
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <Button color="light" size="sm" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button color="danger" size="sm" onClick={handleSave} style={{ fontWeight: 600 }}>Confirm & Save</Button>
          </div>
        </div>
      )}

      {saved && (
        <div className="alert alert-success mt-3 mb-0 text-center" style={{ fontSize: 13, fontWeight: 600, borderRadius: 8 }}>
          <i className="ri-checkbox-circle-line me-2" />Policy saved successfully
        </div>
      )}
    </>
  );
};

// ── Other panels ───────────────────────────────────────────────────────────
const ArchivingPanel = ({ archiveEnabled, setArchiveEnabled, handleSave }) => (
  <>
    <h6 className="fw-bold mb-1" style={{ fontSize: 15 }}>Data Archiving</h6>
    <p className="text-muted mb-4" style={{ fontSize: 12, lineHeight: 1.6 }}>
      Automatically move older records to archive storage before deletion to preserve your audit trail.
    </p>
    <div className="p-3 mb-3" style={{
      backgroundColor: "rgba(255,107,53,0.07)", border: "1px solid rgba(255,107,53,0.2)", borderRadius: 8, fontSize: 12,
    }}>
      <i className="ri-information-line me-2" style={{ color: ORANGE }} />
      <span className="text-muted">Data archiving is currently disabled. Enable it to preserve records beyond the active retention window.</span>
    </div>
    <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ border: "1px solid var(--vz-border-color)" }}>
      <div>
        <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>Enable archiving</div>
        <p className="text-muted mb-0" style={{ fontSize: 12 }}>Records will be moved to cold storage instead of deleted.</p>
      </div>
      <div className="form-check form-switch ms-3">
        <input className="form-check-input" type="checkbox" checked={archiveEnabled}
          onChange={async e => {
            const checked = e.target.checked;
            setArchiveEnabled(checked);
            await handleSave({ archiveEnabled: checked });
          }} style={{ width: 40, height: 22, cursor: "pointer" }} />
      </div>
    </div>
  </>
);

const PurgeSchedulePanel = ({
  purgeFrequency,
  setPurgeFrequency,
  purgeTime,
  handleSave,
  setPurgeTime }) => {
  const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
  const [saved, setSaved] = useState(false);
  const handleScheduleSave = async () => {
    await handleSave();
    setSaved(true);

    setTimeout(() => {
      setSaved(false);
    }, 3000);
  };
  return (
    <>
      <h6 className="fw-bold mb-1" style={{ fontSize: 15 }}>Purge Schedule</h6>
      <p className="text-muted mb-4" style={{ fontSize: 12, lineHeight: 1.6 }}>
        Configure when the automated purge job runs to remove records beyond the retention window.
      </p>
      <div className="mb-3">
        <Label className="text-uppercase fw-medium text-muted mb-2" style={{ fontSize: 11, letterSpacing: "0.06em" }}>Run purge at</Label>
        <div className="d-flex gap-2">
          <SelectDropdown value={purgeTime} onChange={setPurgeTime} options={HOURS} width={110} />
          <SelectDropdown value={purgeFrequency} onChange={setPurgeFrequency} options={["Daily", "Weekly", "Monthly"]} width={120} />
        </div>
      </div>
      <div className="d-flex justify-content-end mt-4">
        <Button
          onClick={handleScheduleSave}
          style={{
            backgroundColor: ORANGE,
            borderColor: ORANGE,
            fontWeight: 600,
            fontSize: 13,
            color: "#ffffff"
          }}
        >
          Save Schedule
        </Button>
      </div>

      {saved && (
        <div
          className="alert alert-success mt-3 mb-0 text-center"
          style={{ fontSize: 13, fontWeight: 600, borderRadius: 8 }}
        >
          <i className="ri-checkbox-circle-line me-2" />
          Purge schedule saved successfully
        </div>
      )}
    </>
  );
};

const ExportPanel = ({
  exportEnabled,
  setExportEnabled,
  exportFormat,
  setExportFormat,
  handleSave
}) => {

  return (
    <>
      <h6 className="fw-bold mb-1" style={{ fontSize: 15 }}>Export Before Purge</h6>
      <p className="text-muted mb-4" style={{ fontSize: 12, lineHeight: 1.6 }}>
        Automatically export records to a file before they are permanently deleted.
      </p>
      <div className="d-flex align-items-center justify-content-between p-3 rounded mb-3" style={{ border: "1px solid var(--vz-border-color)" }}>
        <div>
          <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>Enable export before purge</div>
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>A file will be generated before each purge cycle.</p>
        </div>
        <div className="form-check form-switch ms-3">
          <input className="form-check-input" type="checkbox" checked={exportEnabled}
            onChange={async e => {
              const checked = e.target.checked;
              setExportEnabled(checked);
              await handleSave({ exportEnabled: checked });
            }}
            style={{ width: 40, height: 22, cursor: "pointer" }} />
        </div>
      </div>
      <div className="mb-3">
        <Label className="text-uppercase fw-medium text-muted mb-2" style={{ fontSize: 11, letterSpacing: "0.06em" }}>Export format</Label>
        <SelectDropdown value={exportFormat} onChange={async (val) => {
          setExportFormat(val);
          await handleSave({ exportFormat: val });
        }} options={["CSV", "JSON", "NDJSON"]} width={180} />
      </div>
    </>
  );
};

const PANEL_MAP = {
  retention: RetentionPanel,
  archiving: ArchivingPanel,
  purge_schedule: PurgeSchedulePanel,
  export_before_purge: ExportPanel,
};

// ── Main ───────────────────────────────────────────────────────────────────
const LogRetentionPolicy = () => {
  const [activeKey, setActiveKey] = useState("retention");
  const [retentionBadge, setRetentionBadge] = useState("Retain for 3 Months 14 Days");
  const ActivePanel = PANEL_MAP[activeKey];
  const [archiveEnabled, setArchiveEnabled] = useState(false);
  const [purgeFrequency, setPurgeFrequency] = useState("Daily");
  const [purgeTime, setPurgeTime] = useState("02:00");
  const [exportEnabled, setExportEnabled] = useState(false);
  const [exportFormat, setExportFormat] = useState("CSV");
  const [activeRetention, setActiveRetention] = useState({ value: 14, unit: "Days" });
  const [archiveRetention, setArchiveRetention] = useState({ value: 3, unit: "Months" });
  const [loading, setLoading] = useState(true);

  const handleSave = async (override = {}) => {
    try {
      const authUser = JSON.parse(sessionStorage.getItem("authUser") || "{}");
      const token = authUser?.token;

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/retention`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          active_value: activeRetention.value,
          active_unit: activeRetention.unit,
          archive_value: archiveRetention.value,
          archive_unit: archiveRetention.unit,
          archive_enabled: override.archiveEnabled ?? archiveEnabled,
          purge_frequency: purgeFrequency,
          purge_time: purgeTime,
          export_enabled: override.exportEnabled ?? exportEnabled,
          export_format: override.exportFormat ?? exportFormat,
        }),
      });

      const data = await res.json();

      if (data.success) {
  
        setTimeout(() => {
         
        }, 3000);
      }
    } catch (err) {
      console.error("Error saving settings:", err);
    }
  };

  useEffect(() => {
    const active = activeRetention || {};
    const archive = archiveRetention || {};

    const aVal = active.value || 0;
    const aUnit = active.unit || "Days";

    const arVal = archive.value || 0;
    const arUnit = archive.unit || "Days";

    setRetentionBadge(`Retain for ${aVal} ${aUnit} ${arVal} ${arUnit}`);
  }, [activeRetention, archiveRetention]);

  useEffect(() => {
    const fetchRetention = async () => {
      try {
        const authUser = JSON.parse(sessionStorage.getItem("authUser") || "{}");
        const token = authUser?.token;

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/retention`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (data.success && data.retention) {

          setArchiveEnabled(data.retention.archive_enabled ?? false);

          setPurgeFrequency(data.retention.purge_frequency || "Daily");
          setPurgeTime(data.retention.purge_time || "02:00");

          setExportEnabled(data.retention.export_enabled ?? false);
          setExportFormat(data.retention.export_format || "CSV");

          setActiveRetention({
            value: data.retention.active_value || 14,
            unit: data.retention.active_unit || "Days"
          });

          setArchiveRetention({
            value: data.retention.archive_value || 3,
            unit: data.retention.archive_unit || "Months"
          });

        }
      } catch (err) {
        console.error("Error fetching retention:", err);
      }
      setLoading(false); 
    };

    fetchRetention();
  }, []);

if (loading) {
  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center mt-5">
          <Col className="text-center">
            <div className="spinner-border text-warning" role="status"></div>
            <p className="text-muted mt-2">Loading retention settings...</p>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-4">
          <Col>
           <div className="d-flex align-items-center gap-2">
              <div style={chipStyle}>
                <i className="ri-time-line" style={{ ...pageIconStyle, fontSize: 18 }}></i>
              </div>
              <h5 className="fw-bold mb-0" style={pageHeadingStyle}>
                Log Retention Settings
              </h5>
            </div>
            <p className="text-muted mb-0 mt-1" style={{ fontSize: 13 }}>
              View or manage settings for log data retention and purge policy.
            </p>
          </Col>
        </Row>

        <Row>
          <Col xl={5} lg={5} className="mb-4">
            {SETTINGS_ITEMS.map(item => {

              let dynamicBadge = null;

              if (item.key === "retention") {
                dynamicBadge = retentionBadge;
              }

              if (item.key === "archiving") {
                dynamicBadge = archiveEnabled ? "Enabled" : "Disabled";
              }

              if (item.key === "purge_schedule") {
                dynamicBadge = `${purgeFrequency} · ${purgeTime}`;
              }

              if (item.key === "export_before_purge") {
                dynamicBadge = exportEnabled ? `Enabled · ${exportFormat}` : "Disabled";
              }

              return (
                <SettingCard
                  key={item.key}
                  item={item}
                  isSelected={activeKey === item.key}
                  dynamicBadge={dynamicBadge}
                  onClick={() => setActiveKey(item.key)}
                />
              );
            })}
          </Col>

          <Col xl={7} lg={7}>
            <Card style={{
              background: "var(--vz-secondary-bg, #fff)",
              border: `1px solid ${activeKey === "retention" ? "rgba(255,107,53,0.3)" : "var(--vz-border-color)"}`,
              borderRadius: 10,
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}>
              <CardBody style={{ padding: "24px" }}>
                <ActivePanel
                  activeRetention={activeRetention}
                  setActiveRetention={setActiveRetention}
                  archiveRetention={archiveRetention}
                  setArchiveRetention={setArchiveRetention}

                  onBadgeChange={activeKey === "retention" ? setRetentionBadge : undefined}

                  archiveEnabled={archiveEnabled}
                  setArchiveEnabled={setArchiveEnabled}

                  purgeFrequency={purgeFrequency}
                  setPurgeFrequency={setPurgeFrequency}

                  purgeTime={purgeTime}
                  setPurgeTime={setPurgeTime}
                  handleSave={handleSave}

                  exportEnabled={exportEnabled}
                  setExportEnabled={setExportEnabled}

                  exportFormat={exportFormat}
                  setExportFormat={setExportFormat}

                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LogRetentionPolicy;